/**
 * BookableResourceService
 * Service for managing bookable resources with availability checking and view endpoints
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for bookable resource CRUD operations with availability and view endpoints
 */
export class BookableResourceService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? BookableResourceService.DEFAULT_SERVICE_PATH,
            basePath: BookableResourceService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET BY TYPE ====================
    /**
     * Get all bookable resources by type
     * @param typeId - The resource type ID
     * @returns Array of bookable resources matching the type
     */
    async getByType(typeId) {
        const response = await this.getByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get all bookable resources by type with full response
     * @param typeId - The resource type ID
     * @returns ResponseData with array of bookable resources
     */
    getByTypeRaw(typeId) {
        const url = this.buildUrl(`by-type/${typeId}`);
        return this.http.get(url);
    }
    // ==================== GET BY LOCATION ====================
    /**
     * Get all bookable resources by location
     * @param locationId - The location ID
     * @returns Array of bookable resources at the location
     */
    async getByLocation(locationId) {
        const response = await this.getByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all bookable resources by location with full response
     * @param locationId - The location ID
     * @returns ResponseData with array of bookable resources
     */
    getByLocationRaw(locationId) {
        const url = this.buildUrl(`by-location/${locationId}`);
        return this.http.get(url);
    }
    // ==================== GET ACTIVE ====================
    /**
     * Get all active bookable resources
     * @returns Array of active bookable resources
     */
    async getActive() {
        const response = await this.getActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all active bookable resources with full response
     * @returns ResponseData with array of active bookable resources
     */
    getActiveRaw() {
        const url = this.buildUrl('active');
        return this.http.get(url);
    }
    // ==================== CHECK AVAILABILITY ====================
    /**
     * Check availability of a bookable resource for a given time range
     * @param resourceId - The resource ID
     * @param startDateTime - Start date/time as epoch timestamp
     * @param endDateTime - End date/time as epoch timestamp
     * @returns Resource availability result
     */
    async checkAvailability(resourceId, startDateTime, endDateTime) {
        const response = await this.checkAvailabilityRaw(resourceId, startDateTime, endDateTime);
        if (!response.data) {
            throw new Error(`Failed to check availability for resource ${resourceId}`);
        }
        return response.data;
    }
    /**
     * Check availability of a bookable resource for a given time range with full response
     * @param resourceId - The resource ID
     * @param startDateTime - Start date/time as epoch timestamp
     * @param endDateTime - End date/time as epoch timestamp
     * @returns ResponseData with resource availability result
     */
    checkAvailabilityRaw(resourceId, startDateTime, endDateTime) {
        const url = this.buildUrlWithParams(`${resourceId}/availability`, {
            start: startDateTime,
            end: endDateTime,
        });
        return this.http.get(url);
    }
    // ==================== CHANGE STATUS ====================
    /**
     * Change the status of a bookable resource
     * @param resourceId - The resource ID
     * @param statusId - The new status ID
     * @returns Updated bookable resource
     */
    async changeStatus(resourceId, statusId) {
        const response = await this.changeStatusRaw(resourceId, statusId);
        if (!response.data) {
            throw new Error(`Failed to change status for resource ${resourceId}`);
        }
        return response.data;
    }
    /**
     * Change the status of a bookable resource with full response
     * @param resourceId - The resource ID
     * @param statusId - The new status ID
     * @returns ResponseData with updated bookable resource
     */
    changeStatusRaw(resourceId, statusId) {
        const url = this.buildUrl(`${resourceId}/status`);
        return this.http.put(url, { statusId });
    }
    // ==================== VIEW ENDPOINTS ====================
    /**
     * Get bookable resource view by ID
     * @param id - The resource ID
     * @returns Bookable resource model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Bookable resource view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get bookable resource view by ID with full response
     * @param id - The resource ID
     * @returns ResponseData with bookable resource model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all bookable resource list views
     * @returns Array of bookable resource model views
     */
    async getListView() {
        const response = await this.getListViewRaw();
        return response.data ?? [];
    }
    /**
     * Get all bookable resource list views with full response
     * @returns ResponseData with array of bookable resource model views
     */
    getListViewRaw() {
        const url = this.buildUrl('list-view');
        return this.http.get(url);
    }
}
BookableResourceService.DEFAULT_SERVICE_PATH = 'booking-management';
BookableResourceService.BASE_PATH = 'api/bookable-resources';
//# sourceMappingURL=BookableResourceService.js.map